/**
 * @ngdoc directive
 * @name flowReportGroupContainer
 * @module flowingly.runner.reports
 * @description  This component is used to group the content displayed below the group header.
 *               It is responsible for opening and closing the report groups within the list
 * @usage
 * ```
    <flow-report-group-container group="$ctrl.group" is-mobile="$ctrl.isMobile"></flow-report-group-container>
 * ``` 
 * ### Notes
 * See Also: https://bizflo.atlassian.net/wiki/display/TECH/Angular+Report+Components
 * ### Properties
 * #### Inputs
 * * group: this group
 * * isMobile :show mobile view if set,
 */
'use strict';
import { SharedAngular } from '@Client/@types/sharedAngular';
import angular from 'angular';
import { IStateService } from 'angular-ui-router';

angular
  .module('flowingly.runner.reports')
  .component('flowReportGroupContainer', {
    bindings: {
      group: '<', //this group
      isMobile: '<' //show mobile view if set,
    },
    controller: [
      '$state',
      'flowsUtilityService',
      function (
        $state: IStateService,
        flowsUtilityService: SharedAngular.FlowsUtilityService
      ) {
        const $ctrl = this;

        this.$onInit = function () {
          if ($ctrl.group?.flowModels) {
            $ctrl.initializeFlowModels($ctrl.group.flowModels);
          }
        };

        this.$onChanges = function (changes) {
          if (changes?.group?.currentValue) {
            $ctrl.initializeFlowModels(changes.group.currentValue.flowModels);
            $ctrl.onFilterAction();
          }
        };

        this.searchText = '';
        this.usedTagIds = [];
        this.selectedTags = [];
        this.orderedFlowModels = [];

        this.initializeFlowModels = function (flowModels) {
          this.orderedFlowModels = flowsUtilityService.orderItemsAlphabetically(
            flowModels,
            'flowModelName'
          );

          $ctrl.group.flowModels = this.orderedFlowModels;

          $ctrl.usedTagIds = this.orderedFlowModels.flatMap((fm) =>
            fm.parsedTags.length > 0 ? fm.parsedTags.map((tag) => tag.id) : []
          );
        };

        this.onChangeInSearchText = () => {
          this.onFilterAction();
        };

        this.resetSearchInput = () => {
          this.searchText = '';
          this.onChangeInSearchText();
        };

        this.headerClicked = (flowModel) => {
          $state.go('app.runner.report', {
            flowModelId: flowModel.id,
            flowModelName: flowModel.name
          });
        };

        this.onFilterAction = () => {
          const searchTextLower = this.searchText.toLowerCase();

          const filteredFlowModels = this.orderedFlowModels.filter(
            (x) =>
              (x.description?.toLowerCase().includes(searchTextLower) ||
                x.flowModelName?.toLowerCase().includes(searchTextLower)) &&
              (this.selectedTags.length === 0 ||
                x.parsedTags?.some((tag) => this.selectedTags.includes(tag.id)))
          );

          $ctrl.group.flowModels = filteredFlowModels;
        };

        this.onTagFiltersApplied = (selectedTags: string[]) => {
          this.selectedTags = selectedTags;
          this.onFilterAction();
        };
      }
    ],
    controllerAs: '$ctrl',
    template: `<div class="flow-report-list-filter-container">
                  <i class="fa-light fa-magnifying-glass"></i>
                  <div class="flow-report-search-container">
                    <input
                      class="flow-report-search-input"
                      type="text"
                      placeholder="Search by flow name or description"
                      ng-change="$ctrl.onChangeInSearchText()"
                      ng-model="$ctrl.searchText"
                    />
                    <i
                      class="fas fa-times"
                      ng-click="$ctrl.resetSearchInput()"
                      title="Clear"
                      ng-show="$ctrl.searchText !== ''"
                    ></i>
                  </div>

                  <div class="flow-report-tag-filter">
                    <tag-filter used-tag-ids="$ctrl.usedTagIds" 
                                initial-selected-tags="$ctrl.selectedTags"
                                on-apply="$ctrl.onTagFiltersApplied">
                    </tag-filter>
                  </div>
                </div>
                <report-group-container-v2 flow-models="$ctrl.group.flowModels" 
                                           is-mobile="$ctrl.isMobile"
                                           on-header-click="$ctrl.headerClicked"> 
                </report-group-container-v2>`
  });
